import React from 'react';
import { Check, Square, X } from 'lucide-react';

import { UnifiedTableDataType } from '@/components/ui/unifiedTable/UnifiedTable';

type CandidateRowLike = {
  cells?: Array<{ key: string; value: unknown }>;
};

type CandidateProgramConfigLike = {
  newlineFields?: string[];
  newlineFieldLabelPatterns?: RegExp[];
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T\s].+/;
const DATE_FIELD_PATTERN = /(date|dob|updated|created|timestamp|_at|time)$/i;
const DATE_TIME_INPUT_PATTERN = /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/;
const BOOLEAN_TRUE_VALUES = new Set(['1', 'true', 't', 'yes', 'y', 'on']);
const BOOLEAN_FALSE_VALUES = new Set(['0', 'false', 'f', 'no', 'n', 'off']);
const DATE_ONLY_FIELD_PATTERNS = [
  'dob',
  'hearingdate',
  'admitdate',
  'admitteddate',
  'graduationdate',
  'graduateddate',
];

export const getCellValue = (row: CandidateRowLike | null | undefined, field: string): string => {
  if (!row || !Array.isArray(row.cells)) {
    return '';
  }

  const found = row.cells.find((cell) => cell.key === field);
  if (!found) {
    return '';
  }

  if (found.value === null || found.value === undefined) {
    return '';
  }

  return String(found.value);
};

export const shouldPreserveNewlines = (
  config: CandidateProgramConfigLike | undefined,
  field: string,
  label: string,
): boolean => {
  if (!config) {
    return false;
  }

  if (config.newlineFields?.includes(field)) {
    return true;
  }

  if (!config.newlineFieldLabelPatterns || config.newlineFieldLabelPatterns.length === 0) {
    return false;
  }

  for (const pattern of config.newlineFieldLabelPatterns) {
    if (pattern.test(field)) {
      return true;
    }

    if (pattern.test(label)) {
      return true;
    }
  }

  return false;
};

export const formatNewlineCellValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }

  if (!trimmed.includes(',') && !trimmed.includes(';')) {
    return value;
  }

  const parts = trimmed
    .split(/[;,]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    return value;
  }

  return parts.join('\n');
};

export const truncateCellValue = (value: string, maxChars: number): string => {
  if (value.length <= maxChars) {
    return value;
  }

  return `${value.slice(0, maxChars)}...`;
};

export const toLegacyDateString = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (!DATE_ONLY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const [year, month, day] = trimmed.split('-');
  return `${month}/${day}/${year}`;
};

const isLikelyDateField = (field: string, label?: string): boolean => {
  if (DATE_FIELD_PATTERN.test(field)) {
    return true;
  }

  if (label && DATE_FIELD_PATTERN.test(label)) {
    return true;
  }

  return false;
};

const normalizeFieldToken = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const isForcedDateOnlyField = (field: string, label?: string): boolean => {
  const fieldToken = normalizeFieldToken(field);
  const labelToken = normalizeFieldToken(label || '');

  for (const pattern of DATE_ONLY_FIELD_PATTERNS) {
    if (fieldToken.includes(pattern)) {
      return true;
    }

    if (labelToken.includes(pattern)) {
      return true;
    }
  }

  return false;
};

export const mapDbTypeToDataType = (
  dbType: string | null | undefined,
  field: string,
  label?: string,
): UnifiedTableDataType => {
  const normalized = String(dbType || '')
    .trim()
    .toLowerCase();

  if (isForcedDateOnlyField(field, label)) {
    return 'date';
  }

  if (normalized.startsWith('datetime')) {
    return 'datetime';
  }

  if (normalized.startsWith('timestamp')) {
    return 'timestamp';
  }

  if (normalized.startsWith('date')) {
    return 'date';
  }

  if (normalized.startsWith('tinyint(1)')) {
    return 'boolean';
  }

  if (normalized.startsWith('enum(')) {
    const hasYN = normalized.includes("'y'") && normalized.includes("'n'");
    const has01 = normalized.includes("'0'") && normalized.includes("'1'");
    if (hasYN || has01) {
      return 'boolean';
    }

    return 'string';
  }

  if (normalized === 'bool' || normalized === 'boolean' || normalized.startsWith('bit(1)')) {
    return 'boolean';
  }

  if (
    normalized.startsWith('tinyint') ||
    normalized.startsWith('smallint') ||
    normalized.startsWith('mediumint') ||
    normalized.startsWith('int') ||
    normalized.startsWith('bigint') ||
    normalized.startsWith('year')
  ) {
    return 'int';
  }

  if (normalized.startsWith('decimal') || normalized.startsWith('numeric')) {
    return 'decimal';
  }

  if (normalized.startsWith('float') || normalized.startsWith('double') || normalized.startsWith('real')) {
    return 'float';
  }

  if (normalized.includes('text')) {
    return 'text';
  }

  if (
    normalized.includes('char') ||
    normalized.startsWith('enum') ||
    normalized.startsWith('set') ||
    normalized.includes('json')
  ) {
    return 'string';
  }

  if (isLikelyDateField(field, label)) {
    return 'date';
  }

  return 'auto';
};

const isDateDataType = (dataType: UnifiedTableDataType): boolean => dataType === 'date';
const isDateTimeDataType = (dataType: UnifiedTableDataType): boolean =>
  dataType === 'datetime' || dataType === 'timestamp';
export const isBooleanDataType = (dataType: UnifiedTableDataType): boolean =>
  dataType === 'boolean' || dataType === 'bool';
const isNumericDataType = (dataType: UnifiedTableDataType): boolean =>
  dataType === 'number' || dataType === 'int' || dataType === 'float' || dataType === 'decimal';
export const isIntegerDataType = (dataType: UnifiedTableDataType): boolean => dataType === 'int';
export const isTextLikeDataType = (dataType: UnifiedTableDataType): boolean =>
  dataType === 'auto' || dataType === 'string' || dataType === 'text';

type CandidateEditorKind = 'text' | 'date' | 'datetime-local' | 'number' | 'boolean';

export const getEditorKindForDataType = (dataType: UnifiedTableDataType): CandidateEditorKind => {
  if (isDateDataType(dataType)) {
    return 'date';
  }

  if (isDateTimeDataType(dataType)) {
    return 'datetime-local';
  }

  if (isNumericDataType(dataType)) {
    return 'number';
  }

  if (isBooleanDataType(dataType)) {
    return 'boolean';
  }

  return 'text';
};

const padTwo = (value: number): string => String(value).padStart(2, '0');

const toDateInputValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  if (DATE_TIME_PATTERN.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const usDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usDate) {
    const month = usDate[1].padStart(2, '0');
    const day = usDate[2].padStart(2, '0');
    const year = usDate[3];
    return `${year}-${month}-${day}`;
  }

  const epoch = Date.parse(trimmed);
  if (Number.isNaN(epoch)) {
    return '';
  }

  return new Date(epoch).toISOString().slice(0, 10);
};

const toDateTimeLocalInputValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return `${trimmed}T00:00`;
  }

  const directMatch = trimmed.match(DATE_TIME_INPUT_PATTERN);
  if (directMatch) {
    return `${directMatch[1]}T${directMatch[2]}:${directMatch[3]}`;
  }

  const epoch = Date.parse(trimmed);
  if (Number.isNaN(epoch)) {
    return '';
  }

  const date = new Date(epoch);
  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());
  const hours = padTwo(date.getHours());
  const minutes = padTwo(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toDateTimeStorageValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return `${trimmed} 00:00:00`;
  }

  const directMatch = trimmed.match(DATE_TIME_INPUT_PATTERN);
  if (directMatch) {
    const seconds = directMatch[4] || '00';
    return `${directMatch[1]} ${directMatch[2]}:${directMatch[3]}:${seconds}`;
  }

  const epoch = Date.parse(trimmed);
  if (Number.isNaN(epoch)) {
    return '';
  }

  const date = new Date(epoch);
  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());
  const hours = padTwo(date.getHours());
  const minutes = padTwo(date.getMinutes());
  const seconds = padTwo(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export type BooleanStorageFormat = '10' | 'yn';

export const toBooleanStorageValue = (
  value: string,
  format: BooleanStorageFormat = '10',
): '1' | '0' | 'y' | 'n' | '' | null => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  if (BOOLEAN_TRUE_VALUES.has(normalized)) {
    return format === 'yn' ? 'y' : '1';
  }

  if (BOOLEAN_FALSE_VALUES.has(normalized)) {
    return format === 'yn' ? 'n' : '0';
  }

  return null;
};

const toBooleanInputValue = (value: string): string => {
  const normalized = toBooleanStorageValue(value, '10');
  if (normalized === null) {
    return '';
  }

  return normalized;
};

export const toDraftValueForEditor = (value: string, dataType: UnifiedTableDataType): string => {
  if (isDateDataType(dataType)) {
    return toDateInputValue(value);
  }

  if (isDateTimeDataType(dataType)) {
    return toDateTimeLocalInputValue(value);
  }

  if (isBooleanDataType(dataType)) {
    return toBooleanInputValue(value);
  }

  return value;
};

export const normalizeForComparison = (
  value: string,
  dataType: UnifiedTableDataType,
  booleanFormat: BooleanStorageFormat = '10',
): string => {
  if (isDateDataType(dataType)) {
    const normalizedDate = toDateInputValue(value);
    if (normalizedDate) {
      return normalizedDate;
    }

    return value.trim();
  }

  if (isDateTimeDataType(dataType)) {
    const normalizedDateTime = toDateTimeStorageValue(value);
    if (normalizedDateTime) {
      return normalizedDateTime;
    }

    return value.trim();
  }

  if (isBooleanDataType(dataType)) {
    const normalizedBoolean = toBooleanStorageValue(value, booleanFormat);
    if (normalizedBoolean !== null) {
      return normalizedBoolean;
    }

    return value.trim().toLowerCase();
  }

  if (isNumericDataType(dataType)) {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return trimmed;
    }

    if (isIntegerDataType(dataType)) {
      return String(Math.trunc(parsed));
    }

    return String(parsed);
  }

  return value;
};

export const normalizeForSaveValue = (
  value: string,
  dataType: UnifiedTableDataType,
  fieldLabel: string,
  booleanFormat: BooleanStorageFormat = '10',
): { error: string | null; value: string } => {
  if (isDateDataType(dataType)) {
    if (!value.trim()) {
      return { error: null, value: '' };
    }

    const normalizedDate = toDateInputValue(value);
    if (normalizedDate) {
      return { error: null, value: normalizedDate };
    }

    return {
      error: `${fieldLabel} must be a valid date (YYYY-MM-DD).`,
      value,
    };
  }

  if (isDateTimeDataType(dataType)) {
    if (!value.trim()) {
      return { error: null, value: '' };
    }

    const normalizedDateTime = toDateTimeStorageValue(value);
    if (normalizedDateTime) {
      return { error: null, value: normalizedDateTime };
    }

    return {
      error: `${fieldLabel} must be a valid date/time.`,
      value,
    };
  }

  if (isBooleanDataType(dataType)) {
    const normalizedBoolean = toBooleanStorageValue(value, booleanFormat);
    if (normalizedBoolean !== null) {
      return { error: null, value: normalizedBoolean };
    }

    return {
      error: `${fieldLabel} must be true/false, yes/no, or 1/0.`,
      value,
    };
  }

  if (isNumericDataType(dataType)) {
    const trimmed = value.trim();
    if (!trimmed) {
      return { error: null, value: '' };
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return {
        error: `${fieldLabel} must be a valid number.`,
        value,
      };
    }

    if (isIntegerDataType(dataType) && !Number.isInteger(parsed)) {
      return {
        error: `${fieldLabel} must be a whole number.`,
        value,
      };
    }

    if (isIntegerDataType(dataType)) {
      return { error: null, value: String(Math.trunc(parsed)) };
    }

    return { error: null, value: String(parsed) };
  }

  return { error: null, value };
};

export const formatForReadOnlyDisplay = (
  value: string,
  dataType: UnifiedTableDataType,
): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '(empty)';
  }

  if (isDateDataType(dataType)) {
    const normalized = toDateInputValue(trimmed);
    if (!normalized) {
      return value;
    }

    const [year, month, day] = normalized.split('-');
    return `${month}/${day}/${year}`;
  }

  if (isDateTimeDataType(dataType)) {
    const normalizedDate = toDateInputValue(trimmed);
    if (!normalizedDate) {
      return value;
    }

    const [year, month, day] = normalizedDate.split('-');
    return `${month}/${day}/${year}`;
  }

  if (isBooleanDataType(dataType)) {
    const normalized = toBooleanStorageValue(trimmed, '10');
    if (normalized === '1') {
      return 'Yes';
    }

    if (normalized === '0') {
      return 'No';
    }

    return value;
  }

  return value;
};

export const renderBooleanIcon = (value: string): React.ReactNode => {
  const normalized = toBooleanStorageValue(value, '10');
  if (normalized === '1') {
    return <Check className="h-4 w-4 text-emerald-600" />;
  }

  if (normalized === '0') {
    return <X className="h-4 w-4 text-rose-500" />;
  }

  return <Square className="h-4 w-4 text-[var(--neutral-gray-60)]" />;
};

export const renderHighlightedMatch = (text: string, query: string): React.ReactNode => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return text;
  }

  const normalizedText = text.toLowerCase();
  if (!normalizedText.includes(normalizedQuery)) {
    return text;
  }

  const segments: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const matchStart = normalizedText.indexOf(normalizedQuery, cursor);
    if (matchStart < 0) {
      segments.push(text.slice(cursor));
      break;
    }

    if (matchStart > cursor) {
      segments.push(text.slice(cursor, matchStart));
    }

    const matchEnd = matchStart + normalizedQuery.length;
    segments.push(
      <mark
        key={`${matchStart}-${matchEnd}`}
        className="rounded-sm bg-amber-200 px-0.5 text-inherit"
      >
        {text.slice(matchStart, matchEnd)}
      </mark>,
    );
    cursor = matchEnd;
  }

  return <>{segments}</>;
};

export const isLargeTextField = (dataType: UnifiedTableDataType, maxLength: number): boolean => {
  if (dataType === 'text') {
    return true;
  }

  if (maxLength > 255) {
    return true;
  }

  return false;
};
